import { SegmentationPredictionToCompare, Frame, Label } from 'models/predictions';
import { ReviewChoice } from '../components/ReviewControls';

export interface TransformedSegmentationData {
  leftSegmentation: Label[];
  rightSegmentation: Label[];
  frame: Frame;
}

export const transformSegmentationData = (
  segmentationPrediction: SegmentationPredictionToCompare,
  isBlinded: boolean
): TransformedSegmentationData => {
  let leftSegmentation: Label[] = [];
  let rightSegmentation: Label[] = [];
  const frame = segmentationPrediction.frame;

  // New API: frame-centric responses include `segmentations: [{ label, prediction, segmentationClass, ... }]`
  if (
    (segmentationPrediction as any).segmentations &&
    Array.isArray((segmentationPrediction as any).segmentations)
  ) {
    const segs = (segmentationPrediction as any).segmentations as any[];
    // Non-blinded: left = labels (ground truth), right = predictions (AI)
    leftSegmentation = segs
      .map(s => {
        const l = s.label;
        if (!l) return null;
        // ensure segmentationColor and class from parent segmentation are preserved
        const color =
          s.segmentationColor ?? s.segmentation_color ?? l.segmentationColor ?? undefined;
        const segClass = s.segmentationClass ?? s.segmentationClassValue ?? undefined;
        return {
          ...l,
          segmentationColor: color,
          segmentationClassValue: segClass,
        } as Label & { segmentationColor?: string; segmentationClassValue?: string };
      })
      .filter(Boolean) as Label[];

    rightSegmentation = segs
      .map(s => {
        const p = s.prediction;
        if (!p) return null;
        const color =
          s.segmentationColor ?? s.segmentation_color ?? p.segmentationColor ?? undefined;
        const segClass = s.segmentationClass ?? s.segmentationClassValue ?? undefined;
        return {
          ...p,
          segmentationColor: color,
          segmentationClassValue: segClass,
        } as Label & { segmentationColor?: string; segmentationClassValue?: string };
      })
      .filter(Boolean) as Label[];
    return { leftSegmentation, rightSegmentation, frame };
  }

  // Newer API: per-active-region responses include `allRegions` (all masks on frame) and `activeRegion`.
  // Prefer this shape when present so UI can render all regions and highlight the active one.
  if (
    (segmentationPrediction as any).allRegions &&
    Array.isArray((segmentationPrediction as any).allRegions)
  ) {
    const regions = (segmentationPrediction as any).allRegions as any[];
    // Normalize all regions once and display the full set on both sides so the UI can
    // show every region on the frame while still highlighting the active one.
    const normalizedRegions = regions.map(r => ({
      ...r,
      icid: r.icid || r.uuid,
      segmentationColor: r.segmentationColor ?? r.segmentation_color ?? undefined,
      segmentationClassValue: r.segmentationClass ?? r.segmentation_class ?? undefined,
      paths: r.paths || r.paths,
    }));

    leftSegmentation = normalizedRegions.slice();
    rightSegmentation = normalizedRegions.slice();

    return { leftSegmentation, rightSegmentation, frame };
  }

  // New shape: `label` and/or `prediction` may include their own `regions` arrays
  // (the primary label/prediction may also be present as top-level `label.paths` / `prediction.paths`).
  // Normalize and expose all regions on their respective sides so the UI can render
  // highlighted (primary) paths while still displaying the other regions.
  if (
    ((segmentationPrediction as any).label &&
      Array.isArray((segmentationPrediction as any).label.regions)) ||
    ((segmentationPrediction as any).prediction &&
      Array.isArray((segmentationPrediction as any).prediction.regions))
  ) {
    const labelRegions = ((segmentationPrediction as any).label?.regions || []) as any[];
    const predRegions = ((segmentationPrediction as any).prediction?.regions || []) as any[];

    const normalizeRegion = (r: any) => {
      let rawPaths: any[] = [];
      if (Array.isArray(r.paths)) {
        rawPaths = r.paths;
      } else if (typeof r.paths === 'string') {
        rawPaths = [r.paths];
      } else {
        rawPaths = [];
      }
      const paths = rawPaths.map((p: any) => (p == null ? '' : String(p))).filter(Boolean);
      return {
        ...r,
        icid: r.icid || r.uuid,
        segmentationColor: r.segmentationColor ?? r.segmentation_color ?? undefined,
        segmentationClassValue: r.segmentationClass ?? r.segmentation_class ?? undefined,
        paths,
      };
    };

    // Include top-level primary label/prediction as a region if present (so UI shows highlighted primary)
    const spAny = segmentationPrediction as any;
    const primaryLabel = spAny.label
      ? {
          ...spAny.label,
          icid: spAny.label.icid || spAny.label.uuid,
          segmentationColor: spAny.label.segmentationColor ?? spAny.label.segmentation_color,
          segmentationClassValue:
            spAny.segmentationClass ??
            spAny.label.segmentationClass ??
            spAny.label.segmentation_class,
          paths: Array.isArray(spAny.label.paths)
            ? spAny.label.paths
            : spAny.label.paths
              ? [spAny.label.paths]
              : [],
        }
      : null;

    const primaryPred = spAny.prediction
      ? {
          ...spAny.prediction,
          icid: spAny.prediction.icid || spAny.prediction.uuid,
          segmentationColor:
            spAny.prediction.segmentationColor ?? spAny.prediction.segmentation_color,
          segmentationClassValue:
            spAny.segmentationClass ??
            spAny.prediction.segmentationClass ??
            spAny.prediction.segmentation_class,
          paths: Array.isArray(spAny.prediction.paths)
            ? spAny.prediction.paths
            : spAny.prediction.paths
              ? [spAny.prediction.paths]
              : [],
        }
      : null;

    // Normalize and assemble final lists, deduplicating by icid (primary first)
    const normLabelRegions = labelRegions.map(normalizeRegion);
    const normPredRegions = predRegions.map(normalizeRegion);

    const mergeUniqueByIc = (primary: any | null, others: any[]) => {
      const map = new Map<string, any>();
      if (primary && primary.icid) {
        map.set(primary.icid, normalizeRegion(primary));
      }
      for (const r of others) {
        const nr = normalizeRegion(r);
        if (nr.icid && !map.has(nr.icid)) map.set(nr.icid, nr);
      }
      return Array.from(map.values());
    };

    leftSegmentation = mergeUniqueByIc(primaryLabel, normLabelRegions);
    rightSegmentation = mergeUniqueByIc(primaryPred, normPredRegions);

    return { leftSegmentation, rightSegmentation, frame };
  }

  if (isBlinded) {
    // Blinded mode: only use segmentationA and segmentationB
    leftSegmentation = segmentationPrediction.segmentationA
      ? [{ ...segmentationPrediction.segmentationA }]
      : [];
    rightSegmentation = segmentationPrediction.segmentationB
      ? [{ ...segmentationPrediction.segmentationB }]
      : [];
  } else {
    // Standard mode: only use label and prediction (single)
    leftSegmentation = segmentationPrediction.label ? [{ ...segmentationPrediction.label }] : [];
    rightSegmentation = segmentationPrediction.prediction
      ? [{ ...segmentationPrediction.prediction }]
      : [];
  }

  return { leftSegmentation, rightSegmentation, frame };
};

export const getExistingReview = (
  segmentationPrediction: SegmentationPredictionToCompare,
  isBlinded = false
) => {
  let leftReview, rightReview;

  if (isBlinded) {
    // Blinded mode: use segmentationA (left) and segmentationB (right)
    leftReview = segmentationPrediction.segmentationA?.review;
    rightReview = segmentationPrediction.segmentationB?.review;
  } else {
    // Standard mode: use label (left) and prediction (right)
    leftReview = segmentationPrediction.label?.review;
    rightReview = segmentationPrediction.prediction?.review;
  }

  // If we have both reviews, determine the choice based on their decisions
  if (leftReview && rightReview) {
    const leftAccepted = leftReview.decision === 'ACCEPTED';
    const rightAccepted = rightReview.decision === 'ACCEPTED';

    if (leftAccepted && rightAccepted) {
      return {
        choice: ReviewChoice.ACCEPT_BOTH,
        comment: leftReview.comment || rightReview.comment,
      };
    }

    if (!leftAccepted && !rightAccepted) {
      return {
        choice: ReviewChoice.ACCEPT_NEITHER,
        comment: leftReview.comment || rightReview.comment,
      };
    }

    if (leftAccepted) {
      return {
        choice: ReviewChoice.ACCEPT_LEFT, // Left side accepted
        comment: leftReview.comment,
      };
    }

    if (rightAccepted) {
      return {
        choice: ReviewChoice.ACCEPT_RIGHT, // Right side accepted
        comment: rightReview.comment,
      };
    }
  }

  // Fallback to legacy logic if needed
  if (segmentationPrediction.isAcceptAi) {
    return {
      choice: ReviewChoice.ACCEPT_LEFT,
      comment: segmentationPrediction.reviewComment,
    };
  }

  if (segmentationPrediction.isAcceptHuman) {
    return {
      choice: ReviewChoice.ACCEPT_RIGHT,
      comment: segmentationPrediction.reviewComment,
    };
  }

  if (segmentationPrediction.isAcceptBoth) {
    return {
      choice: ReviewChoice.ACCEPT_BOTH,
      comment: segmentationPrediction.reviewComment,
    };
  }

  if (segmentationPrediction.isAcceptNeither) {
    return {
      choice: ReviewChoice.ACCEPT_NEITHER,
      comment: segmentationPrediction.reviewComment,
    };
  }
};

export const shouldShowPrediction = (
  segmentationPrediction: SegmentationPredictionToCompare,
  isBlinded: boolean
): boolean => {
  if (isBlinded) {
    // Blinded mode: only show if we have segmentationA or segmentationB
    return !!(segmentationPrediction.segmentationA || segmentationPrediction.segmentationB);
  } else {
    // Standard mode: only show if we have label or prediction
    return !!(segmentationPrediction.label || segmentationPrediction.prediction);
  }
};
